import { useCallback, useEffect, useRef, useState } from "react";
import { INDIA_CUSTOMER_MAP_SVG } from "../data/indiaCustomerMapSvg";
import {
  NCR_CHAIN,
  PRESENCE_BY_KEY,
  PRESENCE_LOCATIONS,
  OFFICE_TYPE,
} from "../data/presenceMapLocations";

const PRESENCE_POPUP_W = 200;
const PRESENCE_POPUP_H = 90;
const SVG_NS = "http://www.w3.org/2000/svg";

const INDIA_LOCATIONS = PRESENCE_LOCATIONS.filter(
  (loc) => loc.type !== OFFICE_TYPE.GLOBAL,
);

function svgEl(tag, attrs) {
  const el = document.createElementNS(SVG_NS, tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
  return el;
}

function createPresencePin(loc) {
  const isOffice = loc.type === OFFICE_TYPE.HEAD || loc.type === OFFICE_TYPE.BRANCH;
  const { x, y } = loc;

  const outerR = isOffice ? 17 : 13;
  const ringR = isOffice ? 10 : 7.5;
  const coreR = isOffice ? 4.5 : 3.2;
  const pinR = isOffice ? 5.5 : 4;
  const stemTop = y - (isOffice ? 10 : 8);
  const pinY = stemTop - 5;

  const g = svgEl("g", {
    class: `city-group office-${loc.type}`,
    "data-city": loc.key,
  });

  if (isOffice) {
    g.appendChild(svgEl("circle", { class: "pulse-ring", cx: x, cy: y, r: 7 }));
    g.appendChild(svgEl("circle", { class: "pulse-ring d2", cx: x, cy: y, r: 7 }));
  }

  g.appendChild(svgEl("circle", { class: "city-outer", cx: x, cy: y, r: outerR }));
  g.appendChild(svgEl("circle", { class: "city-ring", cx: x, cy: y, r: ringR }));
  g.appendChild(svgEl("circle", { class: "city-core", cx: x, cy: y, r: coreR }));
  g.appendChild(svgEl("line", {
    class: "pin-stem",
    x1: x,
    y1: stemTop,
    x2: x,
    y2: pinY + pinR,
  }));
  g.appendChild(svgEl("circle", { class: "pin-head", cx: x, cy: pinY, r: pinR }));

  return g;
}

function createPresenceLabel(loc) {
  let labelX = loc.x + 8;
  let labelY = loc.y - 12;
  let anchor = "start";

  if (loc.x < 115) {
    labelX = loc.x + 10;
    labelY = loc.y + 16;
  } else if (loc.x > 360) {
    labelX = loc.x - 8;
    anchor = "end";
  }
  if (loc.y > 520) {
    labelY = loc.y - 18;
  }

  const text = svgEl("text", {
    x: labelX,
    y: labelY,
    "font-family": "Space Mono, monospace",
    fill: "rgba(100,116,139,0.8)",
    "font-size": 8,
    "text-anchor": anchor,
  });
  text.textContent = loc.city;
  return text;
}

function getHubPin(pins) {
  return pins.find((p) => p.key === "delhi") ?? pins[0];
}

function sampleQuadratic(a, control, b, steps = 16) {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const mt = 1 - t;
    points.push({
      x: mt * mt * a.x + 2 * mt * t * control.x + t * t * b.x,
      y: mt * mt * a.y + 2 * mt * t * control.y + t * t * b.y,
    });
  }
  return points;
}

function segmentsIntersect(p1, p2, p3, p4) {
  const cross = (ax, ay, bx, by, cx, cy) => (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const onSegment = (ax, ay, bx, by, cx, cy) =>
    Math.min(ax, bx) <= cx + 1e-6 &&
    cx <= Math.max(ax, bx) + 1e-6 &&
    Math.min(ay, by) <= cy + 1e-6 &&
    cy <= Math.max(ay, by) + 1e-6;

  const d1 = cross(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
  const d2 = cross(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y);
  const d3 = cross(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y);
  const d4 = cross(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  if (Math.abs(d1) < 1e-6 && onSegment(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)) return true;
  if (Math.abs(d2) < 1e-6 && onSegment(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y)) return true;
  if (Math.abs(d3) < 1e-6 && onSegment(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y)) return true;
  if (Math.abs(d4) < 1e-6 && onSegment(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y)) return true;

  return false;
}

function curvesIntersect(samplesA, samplesB) {
  for (let i = 1; i < samplesA.length; i += 1) {
    for (let j = 1; j < samplesB.length; j += 1) {
      const a1 = samplesA[i - 1];
      const a2 = samplesA[i];
      const b1 = samplesB[j - 1];
      const b2 = samplesB[j];
      const sharesEndpoint =
        (Math.hypot(a1.x - b1.x, a1.y - b1.y) < 1.5) ||
        (Math.hypot(a1.x - b2.x, a1.y - b2.y) < 1.5) ||
        (Math.hypot(a2.x - b1.x, a2.y - b1.y) < 1.5) ||
        (Math.hypot(a2.x - b2.x, a2.y - b2.y) < 1.5);
      if (sharesEndpoint) continue;
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

function buildConnectionEdges(pins) {
  const hub = getHubPin(pins);
  const ncrSet = new Set(NCR_CHAIN);
  const targets = pins
    .filter((p) => p.key !== hub.key && !ncrSet.has(p.key))
    .sort(
      (a, b) =>
        Math.atan2(a.y - hub.y, a.x - hub.x) - Math.atan2(b.y - hub.y, b.x - hub.x),
    );

  const edges = [];
  const placed = [];

  targets.forEach((target, index) => {
    const dx = target.x - hub.x;
    const dy = target.y - hub.y;
    const len = Math.hypot(dx, dy) || 1;
    const mx = (hub.x + target.x) / 2;
    const my = (hub.y + target.y) / 2;
    const px = -dy / len;
    const py = dx / len;
    const baseOffset = Math.min(42, Math.max(14, len * 0.1));
    const signOptions = index % 2 === 0 ? [1, -1] : [-1, 1];

    let chosen = null;
    signOptions.some((sign) => {
      const offset = baseOffset + (index % 4) * 4;
      const control = {
        cx: mx + px * offset * sign,
        cy: my + py * offset * sign,
      };
      const samples = sampleQuadratic(hub, { x: control.cx, y: control.cy }, target);
      const overlaps = placed.some((prev) => curvesIntersect(samples, prev.samples));
      if (!overlaps) {
        chosen = { target, control, samples };
        return true;
      }
      return false;
    });

    if (!chosen) {
      chosen = {
        target,
        control: { cx: mx, cy: my },
        samples: sampleQuadratic(hub, { x: mx, y: my }, target),
      };
    }

    edges.push(chosen);
    placed.push(chosen);
  });

  return edges;
}

function ensureConnectionStyles() {
  const styleId = "india-map-connections-style";
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = `
      .conn-path { stroke: rgba(247,152,57,0.95); stroke-width: 2; fill: none; stroke-dasharray: 6 6; opacity: 0.9; }
      .conn-path.ncr-chain { stroke-width: 2.2; opacity: 1; }
      @keyframes conn-dash { to { stroke-dashoffset: -24; } }
      .conn-path.animate { animation: conn-dash 2s linear infinite; }
    `;
    document.head.appendChild(styleEl);
  }
  return styleEl;
}

export default function PresenceIndiaMap({ className = "", zoomed = false }) {
  const mapCardRef = useRef(null);
  const [activeKey, setActiveKey] = useState(null);
  const [popup, setPopup] = useState({ show: false, left: 0, top: 0, flipUp: false });
  const [mapReady, setMapReady] = useState(false);

  const hidePopup = useCallback(() => {
    setPopup((p) => ({ ...p, show: false }));
    setActiveKey(null);
  }, []);

  const showPopup = useCallback((el, key) => {
    const mapCard = mapCardRef.current;
    if (!mapCard || !el || !PRESENCE_BY_KEY[key]) return;

    const wR = mapCard.getBoundingClientRect();
    const eR = el.getBoundingClientRect();
    const cx = eR.left + eR.width / 2 - wR.left;
    const ty = eR.top - wR.top;
    const by = eR.bottom - wR.top;

    let left = cx - 20;
    if (left + PRESENCE_POPUP_W > mapCard.offsetWidth - 8) {
      left = mapCard.offsetWidth - PRESENCE_POPUP_W - 8;
    }
    if (left < 8) left = 8;

    const flipUp = by + PRESENCE_POPUP_H + 14 > mapCard.offsetHeight;

    setActiveKey(key);
    setPopup({
      show: true,
      left,
      top: flipUp ? ty - PRESENCE_POPUP_H - 12 : by + 10,
      flipUp,
    });
  }, []);

  useEffect(() => {
    const mapCard = mapCardRef.current;
    const svg = mapCard?.querySelector("#india-svg");
    if (!svg) return undefined;

    svg.querySelectorAll(".city-group").forEach((g) => g.remove());
    svg.querySelectorAll("text").forEach((t) => t.remove());

    const pinsLayer = svgEl("g", { class: "presence-pins" });
    const labelsLayer = svgEl("g", { class: "presence-labels" });

    INDIA_LOCATIONS.forEach((loc) => {
      pinsLayer.appendChild(createPresencePin(loc));
      labelsLayer.appendChild(createPresenceLabel(loc));
    });

    svg.appendChild(pinsLayer);
    svg.appendChild(labelsLayer);

    const styleEl = ensureConnectionStyles();
    const connGroup = svgEl("g", { class: "connections" });
    svg.appendChild(connGroup);

    const pinMap = Object.fromEntries(
      INDIA_LOCATIONS.map((loc) => [loc.key, { key: loc.key, x: loc.x, y: loc.y }]),
    );

    for (let i = 0; i < NCR_CHAIN.length - 1; i += 1) {
      const from = pinMap[NCR_CHAIN[i]];
      const to = pinMap[NCR_CHAIN[i + 1]];
      if (!from || !to) continue;
      connGroup.appendChild(svgEl("path", {
        d: `M ${from.x} ${from.y} L ${to.x} ${to.y}`,
        class: "conn-path ncr-chain animate",
        "vector-effect": "non-scaling-stroke",
      }));
    }

    const hub = pinMap.delhi;
    const ncrSet = new Set(NCR_CHAIN);
    const remotePins = INDIA_LOCATIONS
      .filter((loc) => loc.key !== "delhi" && !ncrSet.has(loc.key))
      .map((loc) => ({ key: loc.key, x: loc.x, y: loc.y }));

    buildConnectionEdges([hub, ...remotePins]).forEach(({ target, control }) => {
      connGroup.appendChild(svgEl("path", {
        d: `M ${hub.x} ${hub.y} Q ${control.cx} ${control.cy} ${target.x} ${target.y}`,
        class: "conn-path animate",
        "vector-effect": "non-scaling-stroke",
      }));
    });

    setMapReady(true);

    return () => {
      setMapReady(false);
      pinsLayer.remove();
      labelsLayer.remove();
      connGroup.remove();
      if (styleEl?.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, []);

  useEffect(() => {
    if (!mapReady) return undefined;

    const mapCard = mapCardRef.current;
    if (!mapCard) return undefined;

    const groups = mapCard.querySelectorAll(".city-group");
    const cleanups = [];

    groups.forEach((g) => {
      const onEnter = () => showPopup(g, g.dataset.city);
      const onLeave = () => hidePopup();
      g.addEventListener("mouseenter", onEnter);
      g.addEventListener("mouseleave", onLeave);
      cleanups.push(() => {
        g.removeEventListener("mouseenter", onEnter);
        g.removeEventListener("mouseleave", onLeave);
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [mapReady, showPopup, hidePopup]);

  const active = activeKey ? PRESENCE_BY_KEY[activeKey] : null;

  return (
    <div
      className={`presence-india-map${zoomed ? " presence-india-map--zoomed" : ""}${className ? ` ${className}` : ""}`}
      ref={mapCardRef}
    >
      <div
        className={`popup-wrap${popup.show ? " show" : ""} presence-popup-wrap`}
        style={{ left: `${popup.left}px`, top: `${popup.top}px` }}
      >
        {active && (
          <div className={`popup-card presence-popup-card${popup.flipUp ? " arrow-up" : ""}`}>
            <div className="pop-city">{active.city}</div>
            <div className="pop-presence-role">{active.label}</div>
          </div>
        )}
      </div>
      <div className="map-svg-host" dangerouslySetInnerHTML={{ __html: INDIA_CUSTOMER_MAP_SVG }} />
    </div>
  );
}
